import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { CASHBACK_STATUS, TAXID_TYPE } from '@woovi/shared';
import { RewardTicket, BoxFlex } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import type { PaymentLinkCompletedRewards_charge$key } from './__generated__/PaymentLinkCompletedRewards_charge.graphql.ts';

import type { ReactNode } from 'react';

type PaymentLinkCompletedRewardsProps = {
  charge: PaymentLinkCompletedRewards_charge$key;
};
const PaymentLinkCompletedRewards = (
  props: PaymentLinkCompletedRewardsProps,
): ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation();

  const charge = useFragment<PaymentLinkCompletedRewards_charge$key>(
    graphql`
      fragment PaymentLinkCompletedRewards_charge on Charge {
        cashback {
          value
          status
          receiver {
            taxIDObscured {
              taxID
              type
            }
          }
        }
        cashbackExclusive {
          value
          status
          receiver {
            taxIDObscured {
              taxID
              type
            }
          }
        }
        redeemedCashbackExclusive {
          edges {
            node {
              value
              receiver {
                taxIDObscured {
                  taxID
                  type
                }
              }
            }
          }
        }
        payer {
          taxIDObscured {
            taxID
            type
          }
        }
      }
    `,
    props.charge,
  );

  if (!charge) {
    return null;
  }

  const cashbackStatusAllowed = [
    CASHBACK_STATUS.PENDING,
    CASHBACK_STATUS.PENDING_EXCLUSIVE,
    CASHBACK_STATUS.PROCESSING,
    CASHBACK_STATUS.CREATED,
    CASHBACK_STATUS.CONFIRMED,
  ];

  const shouldRenderCashback = () => {
    if (!charge.cashback) {
      return false;
    }

    if (!cashbackStatusAllowed.includes(charge.cashback.status)) {
      return false;
    }

    return true;
  };

  const shouldRenderCashbackExclusive = () => {
    if (!charge.cashbackExclusive) {
      return false;
    }

    if (!cashbackStatusAllowed.includes(charge.cashbackExclusive.status)) {
      return false;
    }

    return true;
  };

  const shouldRenderRedeemedCashbackExclusive = () => {
    if (!charge.redeemedCashbackExclusive) {
      return false;
    }

    if (charge.redeemedCashbackExclusive.edges.length <= 0) {
      return false;
    }

    return true;
  };

  if (
    !shouldRenderCashback() &&
    !shouldRenderCashbackExclusive() &&
    !shouldRenderRedeemedCashbackExclusive()
  ) {
    return null;
  }

  const renderCashback = (): ReactNode => {
    if (!shouldRenderCashback()) {
      return null;
    }

    const value = convertToMoney(charge.cashback?.value || 0);

    const receiver = charge.cashback?.receiver || charge.payer;
    const taxIDObscured = receiver?.taxIDObscured;

    const description =
      taxIDObscured?.type === TAXID_TYPE.BR_CNPJ
        ? t('The cashback has been transferred to your Pix CNPJ key')
        : t('The cashback has been transferred to your Pix CPF key');

    return (
      <RewardTicket
        title={t('Instantaneous cashback')}
        description={`${description}: ${taxIDObscured?.taxID}`}
        value={value}
      />
    );
  };

  const renderCashbackExclusive = (): ReactNode => {
    if (!shouldRenderCashbackExclusive()) {
      return null;
    }

    const value = convertToMoney(charge.cashbackExclusive?.value || 0);

    const receiver = charge.cashbackExclusive?.receiver || charge.payer;
    const taxIDObscured = receiver?.taxIDObscured;

    const description =
      taxIDObscured?.type === TAXID_TYPE.BR_CNPJ
        ? t('To receive it, make a new purchase with the same CNPJ')
        : t('To receive it, make a new purchase with the same CPF');

    return (
      <RewardTicket
        title={t('Cashback on the next purchase')}
        description={`${description}: ${taxIDObscured?.taxID}`}
        value={value}
        color={theme.palette.warning.light}
      />
    );
  };

  const renderRedeemedCashbackExclusive = (): ReactNode => {
    if (!shouldRenderRedeemedCashbackExclusive()) {
      return null;
    }

    const redeemedCashbackValue =
      charge?.redeemedCashbackExclusive.edges.reduce(
        (prev, next) => (next?.node?.value ? prev + next.node.value : prev),
        0,
      );

    const value = convertToMoney(redeemedCashbackValue);

    if (!value) {
      return null;
    }

    const redeemedCashbackExclusive =
      charge?.redeemedCashbackExclusive.edges[0]?.node;

    const receiver = redeemedCashbackExclusive?.receiver || charge.payer;
    const taxIDObscured = receiver?.taxIDObscured;

    const description =
      taxIDObscured?.type === TAXID_TYPE.BR_CNPJ
        ? t('The cashback has been transferred to your Pix CNPJ key')
        : t('The cashback has been transferred to your Pix CPF key');

    return (
      <RewardTicket
        title={t('Cashback Redeemed')}
        description={`${description}: ${taxIDObscured?.taxID}`}
        value={value}
      />
    );
  };

  return (
    <BoxFlex
      sx={{ flexDirection: 'column', alignItems: 'center', gap: '16px' }}
    >
      <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
        {t('Congratulations! You won Cashback 🎉')}
      </Typography>
      {renderCashback()}
      {renderRedeemedCashbackExclusive()}
      {renderCashbackExclusive()}
    </BoxFlex>
  );
};

export default PaymentLinkCompletedRewards;
